import type {
  ObjectToolbarConfig,
  RegisterToolbarConfigArgs,
} from '@/blocks/object-toolbar';

/**
 * The shared `RegisterToolbarConfigArgs` does not model a destructive action, so
 * the bulk-delete opener is added to this builder's own params rather than to the
 * shared type every other register's builder consumes.
 */
type CustomDatasourceRegisterToolbarConfigArgs = RegisterToolbarConfigArgs & {
  delete?: {
    label: string;
    disabled: boolean;
    onClick: () => void;
  };
};

/**
 * Assemble the object-toolbar config for the Custom Datasources register/list
 * page (CUS-172). Register toolbars are type-level: no `parentObject`
 * (permissions resolve against the type) and no `save` (a list page has no
 * form). The register's export (CSV / PDF / custom PDF) is surfaced as the
 * toolbar's "Download" group — one action per `ExportOption` returned by
 * `useExportOptions`, the same source that drives the legacy `ExportButton`.
 * The create button is surfaced as the toolbar's primary action, gated on
 * `insert:custom_datasource`.
 *
 * The Custom Datasources register has no `CustomisableRibbon`, so this builder
 * never surfaces the "Edit Filters" overflow group (it ignores `editRibbon`).
 *
 * The register's bulk delete is surfaced as the toolbar's `danger` group, gated
 * on `delete:custom_datasource`; the toolbar itself labels the group and forces
 * the destructive variant.
 *
 * Pure so it can be unit-tested without rendering the page.
 */
export const buildCustomDatasourceRegisterToolbarConfig = ({
  title,
  counter,
  exportOptions,
  create,
  delete: deleteAction,
}: CustomDatasourceRegisterToolbarConfigArgs): ObjectToolbarConfig => ({
  header: { title, counter },
  actions: {
    ...(create
      ? {
          primary: [
            {
              id: 'create',
              label: create.label,
              iconName: 'plus',
              onClick: create.onClick,
              permission: 'insert:custom_datasource',
            },
          ],
        }
      : {}),
    ...(deleteAction
      ? {
          danger: [
            {
              id: 'delete',
              label: deleteAction.label,
              iconName: 'trash-01',
              disabled: deleteAction.disabled,
              onClick: deleteAction.onClick,
              permission: 'delete:custom_datasource',
            },
          ],
        }
      : {}),
    export: [
      {
        id: 'export',
        actions: exportOptions.map((option) => ({
          id: option.id,
          label: option.text,
          iconName: 'download-01',
          onClick: option.exportFunction,
          disabled: option.disabled,
        })),
      },
    ],
  },
});
